import React, { useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { PurchaseOrderPreview } from '../components/PdfGenerationStudio'
import { purchaseOrderService, projectService, invoiceService, expenseService, salaryService, clientService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import GeneratedPdfPreviewModal from '../components/GeneratedPdfPreviewModal'
import TemplateDocumentPreviewModal from '../components/TemplateDocumentPreviewModal'
import useRolePermissions from '../hooks/useRolePermissions'
import { exportTemplatePreviewPdf } from '../utils/exportTemplatePreviewPdf'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import TruncatedText from '../components/TruncatedText'
import PurchaseOrderCreateModal from '../components/PurchaseOrderCreateModal'
import CollapsibleFilters from '../components/CollapsibleFilters'
import { getBaseUrl } from '../utils/apiConfig'
import { useTemplatePreviewSettings } from '../context/TemplatePreviewContext'

const API_BASE_URL = getBaseUrl()

export default function PurchaseOrders() {
  const { isViewer, canMutate } = useRolePermissions()
  const { tableDensity, fontScale } = useTemplatePreviewSettings()
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [projects, setProjects] = useState([])
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null)
  const [selectedDocument, setSelectedDocument] = useState(null) // Incoming PO upload
  const [viewingPdfPath, setViewingPdfPath] = useState(null)
  const [showFileListModal, setShowFileListModal] = useState(false)
  const [fileListModalData, setFileListModalData] = useState({ files: [], type: '', purchaseOrder: null })
  const [viewingFileInModal, setViewingFileInModal] = useState(null)
  const [templatePreviewData, setTemplatePreviewData] = useState(null)
  const [pdfPreviewPo, setPdfPreviewPo] = useState(null)
  const [pendingTemplateDownload, setPendingTemplateDownload] = useState(null)
  const [messageModal, setMessageModal] = useState({ open: false, title: 'Notification', message: '' })
  const [confirmModal, setConfirmModal] = useState({ open: false, title: 'Confirm', message: '', resolver: null })
  const [poItems, setPoItems] = useState([])
  const [originalPoItems, setOriginalPoItems] = useState([])
  const [poCurrentItem, setPoCurrentItem] = useState({ description: '', price: '', quantity: '' })
  const [poEditingItemId, setPoEditingItemId] = useState(null)
  const [poItemsPage, setPoItemsPage] = useState(1)
  const [poSummary, setPoSummary] = useState(null) // { overhead, rental, material, subcon, petrol, others }
  const [poExpenseSummaries, setPoExpenseSummaries] = useState({}) // Store expense summaries by PO ID
  const [formData, setFormData] = useState({
    project_id: '',
    po_number: '',
    po_date: new Date().toISOString().split('T')[0],
    payment_date: '',
    po_value: '',
    vendor_client_details: '',
    remarks: '',
    remarks_type: 'Outgoing',
    payment_term: '',
    status: 'Unpaid'
  })
  const [filters, setFilters] = useState({
    project_ids: [],
    clientNames: [],
    poNumbers: []
  })
  const [purchaseOrderPage, setPurchaseOrderPage] = useState(1)
  const [marginThreshold, setMarginThreshold] = useState(() => {
    const saved = localStorage.getItem('poMarginThreshold')
    return saved ? Number(saved) : 20
  })
  const { containerRef: purchaseOrderTableRef, rowsPerPage: purchaseOrderRowsPerPage } = useResponsivePageSize({ reservedHeight: 480, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })
  const { containerRef: poItemsTableRef, rowsPerPage: poItemsRowsPerPage } = useResponsivePageSize({ reservedHeight: 640, footerHeight: 24, rowHeight: 48, minRows: 3, maxRows: 6 })

  const generateLineItemId = () => Math.random().toString(36).substr(2, 9)

  const getPoItemsTotal = () => poItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)

  // Calculate profit/loss for a single PO
  const calculatePOProfitLoss = (po) => {
    const poValue = Number(po.po_value || 0)
    const expenseSummary = poExpenseSummaries[String(po.po_id)]

    if (!expenseSummary) {
      // Return 0 if no expense data loaded yet, or return poValue if you want to show full amount
      return 0
    }

    const totalExpenses = Object.values(expenseSummary).reduce((sum, val) => sum + Number(val || 0), 0)
    return poValue - totalExpenses
  }

  // Calculate margin percentage for a single PO: (Profit/Loss / PO Amount) * 100
  const calculatePOMargin = (po) => {
    const poAmount = Number(po.po_value || 0)
    if (poAmount === 0) return 0
    const profitLoss = calculatePOProfitLoss(po)
    return (profitLoss / poAmount) * 100
  }

  // Get reminder text based on margin vs threshold
  const getMarginReminder = (margin) => {
    if (margin >= marginThreshold) return ''
    return '⚠️' // Show warning icon if margin < 20%
  }

  // Count POs with margin below threshold
  const getBelowThresholdMarginCount = () => {
    return filteredPurchaseOrders.filter(po => {
      const margin = calculatePOMargin(po)
      return margin < marginThreshold
    }).length
  }

  const handleMarginThresholdChange = (value) => {
    const numValue = Number(value)
    setMarginThreshold(numValue)
    localStorage.setItem('poMarginThreshold', String(numValue))
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handlePoItemChange = (event) => {
    const { name, value } = event.target
    setPoCurrentItem((prev) => ({ ...prev, [name]: value }))
  }

  const addPoItem = () => {
    if (!poCurrentItem.description || !poCurrentItem.price || !poCurrentItem.quantity) {
      showMessage('Please fill in all detail item fields', 'Validation')
      return
    }

    const amount = parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity)
    setPoItems((prev) => [
      ...prev,
      {
        id: generateLineItemId(),
        description: poCurrentItem.description,
        price: parseFloat(poCurrentItem.price),
        quantity: parseFloat(poCurrentItem.quantity),
        amount
      }
    ])
    setPoCurrentItem({ description: '', price: '', quantity: '' })
  }

  const removePoItem = (id) => {
    setPoItems((prev) => prev.filter((item) => item.id !== id))
  }

  const startPoEditItem = (item) => {
    setPoEditingItemId(item.id)
    setPoCurrentItem({
      description: item.description,
      price: item.price,
      quantity: item.quantity
    })
  }

  const savePoEditItem = (id) => {
    if (!poCurrentItem.description || !poCurrentItem.price || !poCurrentItem.quantity) {
      showMessage('Please fill in all detail item fields', 'Validation')
      return
    }

    const amount = parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity)
    setPoItems((prev) => prev.map((item) => (
      item.id === id
        ? {
            ...item,
            description: poCurrentItem.description,
            price: parseFloat(poCurrentItem.price),
            quantity: parseFloat(poCurrentItem.quantity),
            amount
          }
        : item
    )))
    setPoEditingItemId(null)
    setPoCurrentItem({ description: '', price: '', quantity: '' })
  }

  const cancelPoEditItem = () => {
    setPoEditingItemId(null)
    setPoCurrentItem({ description: '', price: '', quantity: '' })
  }

  useEffect(() => {
    fetchPurchaseOrders()
    fetchProjects()
    fetchInvoices()
  }, [])

  // Load expense summaries for all POs
  useEffect(() => {
    const loadAllPOExpenseSummaries = async () => {
      if (purchaseOrders.length === 0) return

      try {
        const expensesRes = await expenseService.getAll()

        // Salary API calls disabled until backend is fixed
        // const salaryRes = await salaryService.getSummary()
        const salaries = []

        const expenses = expensesRes?.data?.data || []

        const summaries = {}

        for (const po of purchaseOrders) {
          const poId = Number(po.po_id)
          const poNumber = String(po.po_number || '').trim()

          // Filter expenses by PO ID/No. and project_id
          const poExpenses = expenses.filter((e) => {
            const expenseProjectId = Number(e.project_id)
            const expensePoId = Number(e.po_id)
            const expensePoNumber = String(e.po_number || '').trim()
            const expenseAltPoNumber = String(e.po_no || '').trim()
            const expensePurchaseOrderNo = String(e.purchase_order_no || '').trim()

            // Match by PO ID, PO number fields, or project_id (since expenses are linked to projects)
            return expensePoId === poId ||
                   expensePoNumber === poNumber ||
                   expenseAltPoNumber === poNumber ||
                   expensePurchaseOrderNo === poNumber ||
                   expenseProjectId === Number(po.project_id)
          })


          // Calculate expense totals by type
          const expenseTotals = {
            overhead: 0,
            rental: 0,
            material: 0,
            subcon: 0,
            petrol: 0,
            others: 0
          }

          poExpenses.forEach(expense => {
            const type = String(expense.expense_type || '').trim().toLowerCase()
            const amount = Number(expense.amount || 0)
            if (expenseTotals.hasOwnProperty(type)) {
              expenseTotals[type] += amount
            } else {
              expenseTotals.others += amount
            }
          })

          // Calculate overhead from salary - ONLY include salaries specifically tied to this PO
          const poSalaries = salaries.filter(row =>
            Number(row.po_id) === poId
          )

          expenseTotals.overhead = poSalaries.reduce((sum, row) => (
            sum +
            Number(row.salary_amount ?? row.total_amount ?? 0) +
            Number(row.allowance || 0) +
            Number(row.employeer_epf ?? row.employer_epf ?? 0)
          ), 0)

          summaries[String(po.po_id)] = expenseTotals
        }

        setPoExpenseSummaries(summaries)
      } catch (error) {
        console.error('Failed to load PO expense summaries:', error)
      }
    }

    loadAllPOExpenseSummaries()
  }, [purchaseOrders])

  useEffect(() => {
    const handleFocus = () => {
      fetchPurchaseOrders()
      fetchProjects()
      fetchInvoices()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  useEffect(() => {
    const hasPendingOutgoingPOs = purchaseOrders.some((po) => {
      const poType = normalizeTypeLabel(po?.remarks_type)
      const pdfStatus = String(po?.pdf_generation_status || 'ready').toLowerCase()
      return poType === 'Outgoing' && pdfStatus === 'pending'
    })

    if (!hasPendingOutgoingPOs) {
      return undefined
    }

    const timer = setInterval(async () => {
      try {
        const response = await purchaseOrderService.getAll()
        setPurchaseOrders(response.data?.data || [])
      } catch (error) {
        // PDF refresh failed silently
      }
    }, 5000)

    return () => clearInterval(timer)
  }, [purchaseOrders])

  useEffect(() => {
    if (!pendingTemplateDownload?.id) return

    const po = purchaseOrders.find((item) => item.po_id === pendingTemplateDownload.id)
    if (!po) return

    const pdfStatus = String(po.pdf_generation_status || 'ready').toLowerCase()

    if (pdfStatus === 'failed') {
      setPendingTemplateDownload(null)
      showMessage(po.pdf_generation_error || 'PDF generation failed', 'Error')
      return
    }

    if (pdfStatus === 'ready' && po.pdf_path) {
      downloadFile(po.pdf_path, pendingTemplateDownload.fileName || `${po.po_number || 'purchase-order'}.pdf`)
      setPendingTemplateDownload(null)
    }
  }, [purchaseOrders, pendingTemplateDownload])

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await purchaseOrderService.getAll()
      setPurchaseOrders(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch purchase orders')
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const response = await projectService.getAll()
      setProjects(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch projects:', error)
    }
  }

  const fetchInvoices = async () => {
    try {
      const response = await invoiceService.getAll()
      setInvoices(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch invoices for PO status:', error)
    }
  }

  const getDerivedPOStatus = (po) => {
    const poValue = Number(po.po_value || 0)
    if (poValue <= 0) return po?.status === 'Paid' ? 'Paid' : 'Unpaid'

    const normalizeInvoiceType = (inv) => {
      const rawType = inv?.remarks_type || inv?.remarksType || inv?.inv_type || inv?.invType || inv?.invoice_type
      const normalized = String(rawType || '').trim().toLowerCase()
      if (normalized === 'outgoing') return 'Outgoing'
      if (normalized === 'incoming') return 'Incoming'
      return ''
    }

    const isInvoicePaid = (inv) => String(inv?.status || '').trim().toLowerCase() === 'paid'

    // Count ALL invoices for this PO, regardless of Incoming/Outgoing type
    const linkedInvoices = invoices.filter((inv) => {
      const poId = Number(po?.po_id || 0)

      // STRICT: Only match by PO ID - no fallback to PO number matching
      const sameById = inv.po_id && poId && Number(inv.po_id) === poId

      return sameById
    })

    if (linkedInvoices.length === 0) {
      return po?.status === 'Paid' ? 'Paid' : 'Unpaid'
    }

    const issuedTotal = linkedInvoices.reduce((sum, inv) => {
      const amount = Number(inv?.invoice_amount ?? inv?.progress_amount ?? 0)
      return sum + (Number.isFinite(amount) ? amount : 0)
    }, 0)

    const paidTotal = linkedInvoices.reduce((sum, inv) => {
      if (!isInvoicePaid(inv)) return sum
      const amount = Number(inv?.invoice_amount ?? inv?.progress_amount ?? 0)
      return sum + (Number.isFinite(amount) ? amount : 0)
    }, 0)

    const hasPartialClaims = linkedInvoices.some((inv) => {
      const progress = Number.parseFloat(inv?.progress_claim_percentage || 0)
      return Number.isFinite(progress) && progress > 0 && progress < 100
    })

    if (hasPartialClaims) {
      const allLinkedInvoicesPaid = linkedInvoices.every((inv) => isInvoicePaid(inv))
      return allLinkedInvoicesPaid && issuedTotal + 0.000001 >= poValue ? 'Paid' : 'Unpaid'
    }

    return paidTotal + 0.000001 >= poValue ? 'Paid' : 'Unpaid'
  }

  const getIssuedInvoiceClaimForPO = (po) => {
    const poValue = Number(po?.po_value || 0)
    const poId = Number(po?.po_id || 0)
    const poNumber = String(po?.po_number || '').trim()

    const normalizeInvoiceType = (inv) => {
      const rawType = inv?.remarks_type || inv?.remarksType || inv?.inv_type || inv?.invType || inv?.invoice_type
      const normalized = String(rawType || '').trim().toLowerCase()
      if (normalized === 'outgoing') return 'Outgoing'
      if (normalized === 'incoming') return 'Incoming'
      return ''
    }

    // Count ALL invoices for this PO, regardless of Incoming/Outgoing type
    const issuedAmount = invoices.reduce((sum, inv) => {
      // Only match if invoice has po_id that matches the PO's po_id
      const sameById = inv.po_id && poId && Number(inv.po_id) === poId

      if (sameById) {
        const amount = Number(inv?.invoice_amount ?? inv?.progress_amount ?? 0)
        return sum + (Number.isFinite(amount) ? amount : 0)
      }
      return sum
    }, 0)

    const issuedPercent = poValue > 0 ? (issuedAmount / poValue) * 100 : 0

    return {
      issuedAmount,
      issuedPercent
    }
  }

  const showMessage = (message, title = 'Notification') => {
    setMessageModal({ open: true, title, message: String(message || '') })
  }

  const showConfirm = (message, title = 'Confirm') => {
    return new Promise((resolve) => {
      setConfirmModal({
        open: true,
        title,
        message: String(message || ''),
        resolver: resolve
      })
    })
  }

  const handleUpdatePurchaseOrder = async () => {
    try {
      setLoading(true)
      if (poItems.length === 0) {
        showMessage('Please add at least one detail item', 'Validation')
        setLoading(false)
        return
      }

      const normalizedDetailItems = poItems.map((item) => ({
        description: item.description,
        price: parseFloat(item.price),
        quantity: parseFloat(item.quantity),
        amount: parseFloat(item.amount)
      }))

      const resolvedPoValue = parseFloat(getPoItemsTotal().toFixed(2))

      const commonFields = {
        po_date: formData.po_date,
        payment_date: formData.payment_date || '',
        po_value: resolvedPoValue,
        vendor_client_details: formData.vendor_client_details || '',
        remarks: formData.remarks || '',
        remarks_type: formData.remarks_type || '',
        payment_term: formData.payment_term || '',
        status: formData.status || 'Unpaid',
        detail_items: JSON.stringify(normalizedDetailItems)
      }

      if (normalizeTypeLabel(formData.remarks_type) === 'Incoming' && selectedDocument) {
        // Incoming PO with a newly selected file — upload it; backend stores it as pdf_path.
        const formDataWithFile = new FormData()
        Object.entries(commonFields).forEach(([key, value]) => formDataWithFile.append(key, value))
        formDataWithFile.append('po_number', formData.po_number || '')
        formDataWithFile.append('document', selectedDocument)
        await purchaseOrderService.updateWithFile(selectedPurchaseOrder.po_id, formDataWithFile)
      } else {
        await purchaseOrderService.update(selectedPurchaseOrder.po_id, {
          ...commonFields,
          payment_date: formData.payment_date || null,
          detail_items: normalizedDetailItems
        })
      }

      showMessage('Purchase Order updated successfully!', 'Success')
      setSelectedDocument(null)
      await fetchPurchaseOrders()
      closeDetailsModal()
    } catch (error) {
      console.error('Error updating purchase order:', error)
      showMessage('Failed to update purchase order: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetails = async (purchaseOrder) => {
    try {
      setSelectedPurchaseOrder(purchaseOrder)
      setSelectedDocument(null)
      // Format the date properly for the input field (yyyy-MM-dd format)
      const dateString = purchaseOrder.po_date 
        ? new Date(purchaseOrder.po_date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
      
      setFormData({
        project_id: purchaseOrder.project_id,
        po_number: purchaseOrder.po_number,
        po_date: dateString,
        payment_date: purchaseOrder.payment_date ? new Date(purchaseOrder.payment_date).toISOString().split('T')[0] : '',
        po_value: purchaseOrder.po_value,
        vendor_client_details: purchaseOrder.vendor_client_details || '',
        remarks: purchaseOrder.remarks || '',
        payment_term: purchaseOrder.payment_term || ''
        ,remarks_type: purchaseOrder.remarks_type || 'Incoming'
        ,status: purchaseOrder.status || 'Unpaid'
      })

      setShowDetailsModal(true)

      // Load expense summary for this PO:
      // expense types from expenses filtered by PO ID/No., Overhead from payroll (salary + allowance + employer EPF) filtered by PO ID.
      setPoSummary(null)
      try {
        const poId = Number(purchaseOrder.po_id)
        const poNumber = String(purchaseOrder.po_number || '').trim()

        // Load expenses (required)
        const expensesRes = await expenseService.getAll()

        // Salary API calls disabled until backend is fixed
        const salaryRes = null

        // Filter expenses by PO ID/No. and project_id
        const poExpenses = (expensesRes?.data?.data || []).filter((e) => {
          const expenseProjectId = Number(e.project_id)
          const expensePoId = Number(e.po_id)
          const expensePoNumber = String(e.po_number || '').trim()
          const expenseAltPoNumber = String(e.po_no || '').trim()
          const expensePurchaseOrderNo = String(e.purchase_order_no || '').trim()

          // Match by PO ID, PO number fields, or project_id (since expenses are linked to projects)
          return expensePoId === poId ||
                 expensePoNumber === poNumber ||
                 expenseAltPoNumber === poNumber ||
                 expensePurchaseOrderNo === poNumber ||
                 expenseProjectId === Number(purchaseOrder.project_id)
        })

        const sumByType = (type) => poExpenses
          .filter((e) => String(e.expense_category || '').trim().toLowerCase() === type)
          .reduce((sum, e) => sum + Number(e.amount || 0), 0)

        // Filter salary rows by po_id (since backend now supports filtering by PO)
        const salaryRows = (salaryRes?.data?.data || []).filter(row =>
          Number(row.po_id) === poId || row.po_id === null
        )

        const overhead = salaryRows.reduce((sum, row) => (
          sum +
          Number(row.salary_amount ?? row.total_amount ?? 0) +
          Number(row.allowance || 0) +
          Number(row.employeer_epf ?? row.employer_epf ?? 0)
        ), 0)

        setPoSummary({
          overhead: Number(overhead.toFixed(2)),
          rental: Number(sumByType('rental').toFixed(2)),
          material: Number(sumByType('material').toFixed(2)),
          subcon: Number(sumByType('subcon').toFixed(2)),
          petrol: Number(sumByType('petrol').toFixed(2)),
          others: Number(sumByType('others').toFixed(2))
        })
      } catch (summaryError) {
        console.error('Failed to load PO expense summary:', summaryError)
        setPoSummary(null)
      }

      const storedDetailItems = parsePoDetailItems(purchaseOrder.detail_items)
      if (storedDetailItems.length > 0) {
        const loadedItems = storedDetailItems.map((item) => ({
          id: generateLineItemId(),
          description: item.description,
          price: parseFloat(item.price),
          quantity: parseFloat(item.quantity),
          amount: parseFloat(item.amount || item.price * item.quantity)
        }))

        setPoItems(loadedItems)
        setOriginalPoItems(loadedItems)
        setPoCurrentItem({ description: '', price: '', quantity: '' })
      } else {
        setPoItems([])
        setOriginalPoItems([])
      }
    } catch (error) {
      console.error('Error loading purchase order details:', error)
    }
  }

  const handleDeletePurchaseOrder = async (poId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this purchase order?')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await purchaseOrderService.delete(poId)
      showMessage('Purchase Order deleted successfully!', 'Success')
      await fetchPurchaseOrders()
    } catch (error) {
      console.error('Error deleting purchase order:', error)
      showMessage('Failed to delete purchase order: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setSelectedProject(null)
    setShowModal(true)
  }

  const showOutgoingInfo = () => {
    showMessage('This page creates standalone Outgoing purchase orders. Use the create modal to add project, PO, client, and attachment details.', 'Information')
  }

  const closeDetailsModal = () => {
    setShowDetailsModal(false)
    setSelectedPurchaseOrder(null)
    setSelectedDocument(null)
    setPoSummary(null)
      setFormData({
        project_id: '',
        po_number: '',
        po_date: new Date().toISOString().split('T')[0],
        payment_date: '',
        po_value: '',
        vendor_client_details: '',
        remarks: '',
        payment_term: '',
        remarks_type: 'Outgoing',
        status: 'Unpaid'
      })
  }

  const handleViewPDF = (pdfPath) => {
    if (!pdfPath) {
      showMessage('PDF not available for this purchase order', 'Information')
      return
    }
    setViewingPdfPath(`${API_BASE_URL}${pdfPath}`)
  }

  const handleDownloadPreviewPdf = () => {
    if (!viewingPdfPath) return
    const po = pdfPreviewPo || selectedPurchaseOrder
    if (po) {
      downloadPurchaseOrderTemplatePdf(po)
      return
    }
    const pathWithoutBase = viewingPdfPath.replace(API_BASE_URL, '')
    const fallbackName = `${selectedPurchaseOrder?.po_number || 'purchase-order'}.pdf`
    downloadFile(pathWithoutBase, fallbackName)
  }

  const handleDownloadTemplatePreviewPdf = async (previewElement, fileName) => {
    try {
      await exportTemplatePreviewPdf(previewElement, fileName || `${templatePreviewData?.number || 'purchase-order'}.pdf`)
    } catch (error) {
      showMessage(error.message || 'Failed to download template preview PDF', 'Error')
      throw error
    }
  }

  const toNumber = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const normalizeText = (value) => String(value || '').trim().toLowerCase()

  const buildPurchaseOrderTemplateData = async (po) => {
    let resolvedClient = null
    const poClientName = po.vendor_client_details || ''

      if (poClientName) {
        try {
          const clientsResponse = await clientService.getAll()
          const clients = clientsResponse.data && clientsResponse.data.data ? clientsResponse.data.data : []
          const normalizedPoClientName = normalizeText(poClientName)
          resolvedClient = clients.find((client) => normalizeText(client.client_name) === normalizedPoClientName) || null
        } catch (clientError) {
          // Could not resolve client record for PO preview
        }
      }

      const detailItems = parsePoDetailItems(po.detail_items)

      let previewItems = detailItems.map((item, index) => {
        const price = toNumber(item.price)
        const qty = toNumber(item.quantity)
        return {
          id: item.detail_item_id || `row-${index + 1}`,
          description: item.description || '',
          price,
          qty,
          amount: price * qty
        }
      })

      if (previewItems.length === 0) {
        const fallbackAmount = toNumber(po.po_value)
        previewItems = [{
          id: 'fallback-1',
          description: po.remarks || 'Works as per purchase order',
          price: fallbackAmount,
          qty: 1,
          amount: fallbackAmount
        }]
      }

      const totalAmount = previewItems.reduce((sum, item) => sum + toNumber(item.amount), 0)
      const totalQty = previewItems.reduce((sum, item) => sum + toNumber(item.qty), 0)
      const unitPriceAverage = totalQty > 0 ? totalAmount / totalQty : totalAmount

      return {
        id: po.po_id,
        number: po.po_number || 'purchase-order',
        fileName: `${po.po_number || 'purchase-order'}.pdf`,
        template: 'purchaseOrder',
        title: `Purchase Order Preview - ${po.po_number || 'N/A'}`,
        downloadPath: po.pdf_path || null,
        draft: {
          companyName: 'PROBARA GIGIH TRADING (003614861-V)',
          companyAddress1: '(IMG2), JALAN SALAK TINGGI',
          companyAddress2: 'KAMPUNG JENDERAM HULU, 43800 DENGKIL, SELANGOR',
          companyAddress3: 'Email : probaragigih@gmail.com',
          clientName: po.vendor_client_details || '-',
          registrationNo: resolvedClient?.registration_no || '-',
          poNo: po.po_number || '-',
          poIssuedDate: po.po_date ? new Date(po.po_date).toISOString().split('T')[0] : '-',
          remarks: po.remarks || '-',
          paymentTerm: po.payment_term || '-',
          projectName: po?.Project?.project_name || '-',
          paymentMilestone: po.payment_term || '-'
        },
        items: previewItems,
        totals: {
          totalAmount,
          totalQty,
          unitPriceAverage
        }
      }
  }

  const openPurchaseOrderTemplatePreview = async (po) => {
    try {
      setTemplatePreviewData(await buildPurchaseOrderTemplateData(po))
    } catch (error) {
      console.error('Failed to load purchase order template preview:', error)
      showMessage('Failed to open purchase order preview', 'Error')
    }
  }

  // Renders the same PurchaseOrderPreview component offscreen and exports it
  // through the template PDF pipeline, so the downloaded file is identical to
  // the preview shown in the modal.
  const downloadPurchaseOrderTemplatePdf = async (po) => {
    try {
      const templateData = await buildPurchaseOrderTemplateData(po)

      const container = document.createElement('div')
      container.style.cssText = 'position:fixed;left:-99999px;top:0;width:210mm;'
      container.innerHTML = `<div class="template-preview-theme template-density-${tableDensity}"><div class="preview-card preview-card-document"></div></div>`
      container.style.setProperty('--template-font-scale', fontScale)
      document.body.appendChild(container)
      const mountPoint = container.querySelector('.preview-card-document')

      try {
        const root = createRoot(mountPoint)
        root.render(<PurchaseOrderPreview draft={templateData.draft} items={templateData.items} totals={templateData.totals} />)

        // Give React a tick to commit the render before serializing.
        await new Promise((resolve) => setTimeout(resolve, 50))

        await exportTemplatePreviewPdf(mountPoint, templateData.fileName)
        root.unmount()
      } finally {
        document.body.removeChild(container)
      }
    } catch (error) {
      console.error('Failed to download purchase order PDF:', error)
      showMessage(error.message || 'Failed to download purchase order PDF', 'Error')
    }
  }

  const downloadFile = async (filePath, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}${filePath}`)
      if (!response.ok) throw new Error('Download failed')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || filePath.split('/').pop() || 'document'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      showMessage('Failed to download file', 'Error')
    }
  }

  const openFileListModal = (po) => {
    const files = po?.pdf_path
      ? [{ path: po.pdf_path, name: `${po.po_number || 'PO'}.pdf`, templateExport: true }]
      : []

    setFileListModalData({ files, type: 'document', purchaseOrder: po })
    setShowFileListModal(true)
  }

  const viewFileInModal = (filePath, file = null) => {
    const fullPath = `${API_BASE_URL}${filePath}`
    setShowFileListModal(false)
    if (getFileExtension(filePath) === 'pdf') {
      // Generated PDFs download through the template pipeline (WYSIWYG).
      setPdfPreviewPo(file?.templateExport ? (fileListModalData.purchaseOrder || null) : null)
      setViewingPdfPath(fullPath)
      return
    }
    setViewingFileInModal(fullPath)
  }

  const getFileExtension = (filePath) => {
    return filePath?.split('.').pop()?.toLowerCase() || ''
  }

  const FileIconButton = ({ onClick, icon, tooltip, disabled = false }) => {
    const [showTooltip, setShowTooltip] = useState(false)

    return (
      <div className="relative inline-block">
        <button
          onClick={onClick}
          disabled={disabled}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="text-blue-600 hover:text-blue-800 text-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          title={tooltip}
        >
          {icon}
        </button>
        {showTooltip && !disabled && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap pointer-events-none">
            {tooltip}
          </div>
        )}
      </div>
    )
  }

  const getFilteredPurchaseOrders = () => {
    return purchaseOrders.filter(po => {
      const projectId = String(po?.project_id || '')
      const poNumber = String(po?.po_number || '')
      const matchClientName = filters.clientNames.length === 0 || filters.clientNames.includes(po?.vendor_client_details || po?.Project?.company_name || '')
      const matchProject = filters.project_ids.length === 0 || filters.project_ids.includes(projectId)
      const matchPoNumber = filters.poNumbers.length === 0 || filters.poNumbers.includes(poNumber)
      return matchProject && matchClientName && matchPoNumber
    })
  }

  const getUniqueClientNames = () => {
    return [...new Set(purchaseOrders.map((po) => String(po?.vendor_client_details || po?.Project?.company_name || '').trim()).filter(Boolean))].sort()
  }

  const getUniqueProjectOptions = () => {
    return projects.map((project) => ({
      value: String(project.project_id),
      label: `${project.project_code} - ${project.project_name}`
    }))
  }

  const getUniquePoNumbers = () => {
    return [...new Set(purchaseOrders.map((po) => String(po?.po_number || '').trim()).filter(Boolean))].sort()
  }

  const filteredPurchaseOrders = getFilteredPurchaseOrders()
  const totalPurchaseOrderPages = Math.max(1, Math.ceil(filteredPurchaseOrders.length / purchaseOrderRowsPerPage))
  const paginatedPurchaseOrders = filteredPurchaseOrders.slice((purchaseOrderPage - 1) * purchaseOrderRowsPerPage, purchaseOrderPage * purchaseOrderRowsPerPage)
  const totalPoItemPages = Math.max(1, Math.ceil(poItems.length / poItemsRowsPerPage))
  const paginatedPoItems = poItems.slice((poItemsPage - 1) * poItemsRowsPerPage, poItemsPage * poItemsRowsPerPage)

  useEffect(() => {
    setPurchaseOrderPage(1)
  }, [filters, purchaseOrderRowsPerPage])

  useEffect(() => {
    setPurchaseOrderPage((prev) => Math.min(prev, totalPurchaseOrderPages))
  }, [totalPurchaseOrderPages])

  const normalizeTypeLabel = (rawType) => {
    const normalized = String(rawType || '').trim().toLowerCase()
    if (normalized === 'outgoing') return 'Outgoing'
    if (normalized === 'incoming') return 'Incoming'
    return ''
  }

  const parsePoDetailItems = (rawValue) => {
    if (!rawValue) return []
    if (Array.isArray(rawValue)) return rawValue

    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim()
      if (!trimmed) return []
      try {
        const parsed = JSON.parse(trimmed)
        return Array.isArray(parsed) ? parsed : []
      } catch (_error) {
        return []
      }
    }

    return []
  }

  const getTypeBadgeClass = (typeLabel) => {
    if (typeLabel === 'Outgoing') return 'bg-amber-100 text-amber-800'
    if (typeLabel === 'Incoming') return 'bg-sky-100 text-sky-800'
    return 'bg-gray-100 text-gray-700'
  }

  const resetFilters = () => {
    setFilters({
      project_ids: [],
      clientNames: [],
      poNumbers: []
    })
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Purchase Orders</h1>
        <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Margin Threshold:</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={marginThreshold}
                  onChange={(e) => handleMarginThresholdChange(e.target.value)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
              </div>
              {getBelowThresholdMarginCount() > 0 && (
                <span className="relative flex h-5 w-5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-red-600 items-center justify-center text-xs font-bold text-white">
                    {getBelowThresholdMarginCount()}
                  </span>
                </span>
              )}
            </div>
            {canMutate && (
              <button
                type="button"
                onClick={openCreateModal}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
              >
                <span className="flex items-center gap-2">
                  + New Purchase Order
                  <span className="w-5 h-5 rounded-full border border-white/30 text-white text-xs flex items-center justify-center font-bold group-hover:bg-white/20 transition">?</span>
                </span>
                <span className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  Info: outgoing only
                </span>
              </button>
            )}
          </div>
      </div>

      {/* Filter Section */}
      <CollapsibleFilters>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Project</label>
            <MultiSelectFilterDropdown
              options={getUniqueProjectOptions()}
              selectedValues={filters.project_ids}
              onChange={(project_ids) => setFilters((prev) => ({ ...prev, project_ids }))}
              placeholder="All projects"
              emptyText="No projects"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
            <MultiSelectFilterDropdown
              options={getUniqueClientNames()}
              selectedValues={filters.clientNames}
              onChange={(clientNames) => setFilters((prev) => ({ ...prev, clientNames }))}
              placeholder="All clients"
              emptyText="No purchase order records"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PO No.</label>
            <MultiSelectFilterDropdown
              options={getUniquePoNumbers()}
              selectedValues={filters.poNumbers}
              onChange={(poNumbers) => setFilters((prev) => ({ ...prev, poNumbers }))}
              placeholder="All PO numbers"
              emptyText="No PO numbers"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchPurchaseOrders}
        loadingText="Loading purchase order records..."
        containerRef={purchaseOrderTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">PO No.</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Type</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Status</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Project Name</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Client Name</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Profit / Loss</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Margin</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Reminder</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Amount</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Invoiced</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Outstanding</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Claim Progress</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Issued Date</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Payment Date</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Document</th>
              {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredPurchaseOrders.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '15' : '16'} className="px-3 py-2 text-center text-gray-500">
                  No purchase orders yet. Create your first one!
                </td>
              </tr>
            ) : (
              paginatedPurchaseOrders.map((po) => {
                // Use project data from the PO response (already included by API)
                const projectData = po.Project || {}
                const poType = normalizeTypeLabel(po.remarks_type)
                const claim = getIssuedInvoiceClaimForPO(po)
                const poAmount = Number(po.po_value || 0)
                const outstandingAmount = Math.max(0, poAmount - Number(claim.issuedAmount || 0))
                const profitLoss = calculatePOProfitLoss(po)
                const margin = calculatePOMargin(po)
                const reminder = getMarginReminder(margin)
                return (
                  <tr key={po.po_id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(po)}>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap"><TruncatedText text={po.po_number} maxChars={20} className="font-semibold text-blue-600" /></td>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeBadgeClass(poType)}`}>
                        {poType || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        getDerivedPOStatus(po) === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {getDerivedPOStatus(po)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap"><TruncatedText text={projectData?.project_name} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm text-left whitespace-nowrap"><TruncatedText text={po.vendor_client_details} fallback="N/A" /></td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap font-semibold">
                      <span className={profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formatCurrency(profitLoss)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap font-semibold">
                      <span className={margin >= marginThreshold ? 'text-green-600' : 'text-red-600'}>
                        {margin.toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{reminder || '-'}</td>
                    <td className="px-3 py-2 text-sm font-medium text-center whitespace-nowrap">{formatCurrency(po.po_value)}</td>
                    <td className="px-3 py-2 text-sm font-medium text-center text-green-600 whitespace-nowrap">{formatCurrency(claim.issuedAmount)}</td>
                    <td className="px-3 py-2 text-sm font-medium text-center text-orange-600 whitespace-nowrap">{formatCurrency(outstandingAmount)}</td>
                    <td className="px-3 py-2 text-sm font-medium text-center whitespace-nowrap">{claim.issuedPercent.toFixed(2)}%</td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{formatDate(po.po_date)}</td>
                    <td className="px-3 py-2 text-sm text-center whitespace-nowrap">{po.payment_date ? formatDate(po.payment_date) : '-'}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-2">
                        {poType === 'Outgoing' ? (
                          <FileIconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              openPurchaseOrderTemplatePreview(po)
                            }}
                            icon="✨"
                            tooltip="Open Preview"
                          />
                        ) : null}
                        {poType === 'Incoming' && po.pdf_path ? (
                          <FileIconButton
                            onClick={(e) => {
                              e.stopPropagation()
                              openFileListModal(po)
                            }}
                            icon="📎"
                            tooltip="View Generated PDF"
                          />
                        ) : null}
                        {(!poType || (poType === 'Incoming' && !po.pdf_path)) && (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    {!isViewer && (
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleViewDetails(po)
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeletePurchaseOrder(po.po_id)
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
            {filteredPurchaseOrders.length > 0 && (
              <tfoot className="sticky bottom-0 bg-gray-100 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                <tr>
                  <td colSpan={5} className="px-3 py-2.5 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Total ({filteredPurchaseOrders.length} purchase order{filteredPurchaseOrders.length !== 1 ? 's' : ''}) — Net Profit / Loss:
                  </td>
                  <td className="px-3 py-2.5 text-center text-sm font-bold text-gray-900 whitespace-nowrap">
                    <span className={filteredPurchaseOrders.reduce((sum, po) => sum + calculatePOProfitLoss(po), 0) >= 0 ? 'text-green-900' : 'text-red-900'}>
                      {formatCurrency(filteredPurchaseOrders.reduce((sum, po) => sum + calculatePOProfitLoss(po), 0))}
                    </span>
                  </td>
                  <td colSpan={isViewer ? 9 : 10} />
                </tr>
              </tfoot>
            )}
        </table>
        <TablePagination
          currentPage={purchaseOrderPage}
          totalPages={totalPurchaseOrderPages}
          totalItems={filteredPurchaseOrders.length}
          rowsPerPage={purchaseOrderRowsPerPage}
          onPrevious={() => setPurchaseOrderPage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setPurchaseOrderPage((prev) => Math.min(prev + 1, totalPurchaseOrderPages))}
        />
      </TableStateWrapper>

      <PurchaseOrderCreateModal
        isOpen={showModal && !showDetailsModal}
        onClose={() => setShowModal(false)}
        projects={projects}
        defaultProjectId={selectedProject?.project_id || ''}
        onCreated={fetchPurchaseOrders}
      />

      {/* Purchase Order Details Modal */}
      {showDetailsModal && selectedPurchaseOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] " onClick={() => closeDetailsModal()}>
          <div className="modal-panel-standard bg-white max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">Edit Purchase Order No. {selectedPurchaseOrder.po_number}</h2>
              <button
                type="button"
                onClick={closeDetailsModal}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {/* Expense Summary */}
            {(() => {
              const summaryItems = [
                { label: 'Overhead', value: poSummary?.overhead || 0 },
                { label: 'Rental', value: poSummary?.rental || 0 },
                { label: 'Materials', value: poSummary?.material || 0 },
                { label: 'Subcon', value: poSummary?.subcon || 0 },
                { label: 'Petrol', value: poSummary?.petrol || 0 },
                { label: 'Others', value: poSummary?.others || 0 }
              ]
              const grandTotal = summaryItems.reduce((sum, item) => sum + Number(item.value || 0), 0)
              const poAmount = Number(formData.po_value || selectedPurchaseOrder?.po_value || 0)
              const profitLoss = poAmount - grandTotal
              return (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {summaryItems.map((item) => (
                      <div key={item.label} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                        <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                        <p className="text-sm font-semibold text-gray-800">{formatCurrency(item.value)}</p>
                      </div>
                    ))}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                      <p className="text-xs text-blue-700 mb-1">Total Expenses</p>
                      <p className="text-sm font-bold text-blue-800">{formatCurrency(grandTotal)}</p>
                    </div>
                    <div className={`rounded-lg border px-3 py-2 ${profitLoss >= 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      <p className={`text-xs mb-1 ${profitLoss >= 0 ? 'text-green-700' : 'text-red-700'}`}>Profit / Loss</p>
                      <p className={`text-sm font-bold ${profitLoss >= 0 ? 'text-green-800' : 'text-red-800'}`}>{formatCurrency(profitLoss)}</p>
                    </div>
                  </div>
                </div>
              )
            })()}

            {(() => {
              const currentPOType = normalizeTypeLabel(formData.remarks_type)
              return (
                <div className={`mb-6 p-4 rounded-lg border ${
                  currentPOType === 'Incoming'
                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                    : currentPOType === 'Outgoing'
                      ? 'bg-amber-50 border-amber-200 text-amber-800'
                      : 'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  <p className="font-medium">
                    {currentPOType === 'Incoming'
                      ? 'Incoming PO: document section shows uploaded file view only.'
                      : currentPOType === 'Outgoing'
                        ? 'Outgoing PO: document section shows generated PDF view only.'
                        : 'PO type is not set. Document section follows type-based rules when type is available.'}
                  </p>
                </div>
              )
            })()}
            
            <div className="modal-form-grid-standard">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO No.</label>
                <input
                  type="text"
                  name="po_number"
                  value={formData.po_number}
                  onChange={handleInputChange}
                  placeholder="eg. PO-2024-001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                <input
                  type="text"
                  value={selectedPurchaseOrder?.Project?.project_name || 'N/A'}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO Value (RM) <span className="text-red-600">*</span></label>
                <input
                  type="number"
                  name="po_value"
                  value={formData.po_value}
                  onChange={handleInputChange}
                  step="0.01"
                  readOnly={normalizeTypeLabel(formData.remarks_type) === 'Outgoing'}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    normalizeTypeLabel(formData.remarks_type) === 'Outgoing'
                      ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                      : 'focus:ring-2 focus:ring-blue-600 focus:border-transparent'
                  }`}
                />
                {normalizeTypeLabel(formData.remarks_type) === 'Outgoing' && (
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated from Detail Items total</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date <span className="text-red-600">*</span></label>
                <input
                  type="date"
                  name="po_date"
                  value={formData.po_date}
                  onChange={handleInputChange}
                  className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                <input
                  type="date"
                  name="payment_date"
                  value={formData.payment_date || ''}
                  onChange={handleInputChange}
                  className="modal-date-input px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-set when claim progress reaches 100%</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vendor/Client Details</label>
                <input
                  type="text"
                  name="vendor_client_details"
                  value={formData.vendor_client_details}
                  onChange={handleInputChange}
                  placeholder="Vendor or client name/details"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                placeholder="Additional remarks..."
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Term</label>
              <input
                type="text"
                name="payment_term"
                value={formData.payment_term}
                onChange={handleInputChange}
                placeholder="e.g. Net 30, COD, Due on Completion"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PO Type</label>
                <select
                  name="remarks_type"
                  value={formData.remarks_type}
                  onChange={handleInputChange}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                >
                  <option value="">Select...</option>
                  <option value="Outgoing">Outgoing</option>
                  <option value="Incoming">Incoming</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                >
                  <option value="Unpaid">Unpaid</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
            </div>

            {(() => {
              const currentPOType = normalizeTypeLabel(formData.remarks_type)

              if (currentPOType === 'Outgoing') {
                return (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Generated PDF</label>
                    {selectedPurchaseOrder?.pdf_path ? (
                      <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm">
                          ✓ Generated file available: <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              openPurchaseOrderTemplatePreview(selectedPurchaseOrder)
                            }}
                            className="text-green-600 hover:text-green-800 underline font-medium"
                          >
                            Preview Generated PDF 📋
                          </button>
                        </p>
                      </div>
                    ) : (
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-gray-600 text-sm">No generated PDF found for this outgoing PO.</p>
                      </div>
                    )}

                    <div className="border-t pt-4 mt-6 space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-blue-800 text-sm">
                          Edit project detail items here. Clicking Update will regenerate outgoing PO PDF using these latest items.
                        </p>
                      </div>

                      <div ref={poItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 border-b border-gray-300">
                            <tr>
                              <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                              <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                              <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                              <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                              <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                              {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedPoItems.map((item, idx) => (
                              <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(poItemsPage - 1) * poItemsRowsPerPage + idx + 1}</td>
                                <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                                <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                                <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                                <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                                {!isViewer && (
                                  <td className="px-2 py-1 text-center space-x-2">
                                    <button
                                      type="button"
                                      onClick={() => startPoEditItem(item)}
                                      className="inline-flex items-center justify-center w-8 h-8 text-blue-500 hover:text-blue-600 transition"
                                      title="Edit item"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removePoItem(item.id)}
                                      className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition"
                                      title="Delete item"
                                    >
                                      ❌
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                            {poEditingItemId === null && !isViewer && (
                              <tr className="border-b border-gray-200 bg-blue-50">
                                <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{poItems.length + 1}</td>
                                <td className="px-2 py-1">
                                  <textarea
                                    name="description"
                                    value={poCurrentItem.description}
                                    onChange={handlePoItemChange}
                                    placeholder="Item description"
                                    rows="2"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    name="price"
                                    value={poCurrentItem.price}
                                    onChange={handlePoItemChange}
                                    placeholder="0.00"
                                    step="0.01"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    name="quantity"
                                    value={poCurrentItem.quantity}
                                    onChange={handlePoItemChange}
                                    placeholder="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                  />
                                </td>
                                <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                                  {poCurrentItem.price && poCurrentItem.quantity
                                    ? formatCurrency(parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity))
                                    : formatCurrency(0)}
                                </td>
                                <td className="px-2 py-1 text-center">
                                  <button
                                    type="button"
                                    onClick={addPoItem}
                                    title="Add new item"
                                    className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 transition"
                                  >
                                    ➕
                                  </button>
                                </td>
                              </tr>
                            )}
                            {poEditingItemId !== null && (
                              <tr className="border-b border-gray-200 bg-green-50">
                                <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-green-100 whitespace-nowrap">{poItems.findIndex((item) => item.id === poEditingItemId) + 1}</td>
                                <td className="px-2 py-1">
                                  <textarea
                                    name="description"
                                    value={poCurrentItem.description}
                                    onChange={handlePoItemChange}
                                    placeholder="Item description"
                                    rows="2"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    name="price"
                                    value={poCurrentItem.price}
                                    onChange={handlePoItemChange}
                                    placeholder="0.00"
                                    step="0.01"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    name="quantity"
                                    value={poCurrentItem.quantity}
                                    onChange={handlePoItemChange}
                                    placeholder="0"
                                    step="0.01"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                  />
                                </td>
                                <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                                  {poCurrentItem.price && poCurrentItem.quantity
                                    ? formatCurrency(parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity))
                                    : formatCurrency(0)}
                                </td>
                                <td className="px-2 py-1 text-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => savePoEditItem(poEditingItemId)}
                                    title="Save changes"
                                    className="inline-flex items-center justify-center w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded transition"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelPoEditItem}
                                    title="Cancel editing"
                                    className="inline-flex items-center justify-center w-8 h-8 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            )}
                            {poItems.length === 0 && (
                              <tr>
                                <td colSpan={isViewer ? '5' : '6'} className="px-3 py-2 text-center text-gray-500">No detail items yet.</td>
                              </tr>
                            )}
                            {poItems.length > 0 && (
                              <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                                <td colSpan="4" className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                                <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getPoItemsTotal())}</td>
                                {!isViewer && <td></td>}
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <TablePagination
                          currentPage={poItemsPage}
                          totalPages={totalPoItemPages}
                          totalItems={poItems.length}
                          rowsPerPage={poItemsRowsPerPage}
                          onPrevious={() => setPoItemsPage((prev) => Math.max(prev - 1, 1))}
                          onNext={() => setPoItemsPage((prev) => Math.min(prev + 1, totalPoItemPages))}
                        />
                      </div>
                    </div>
                  </div>
                )
              }

              if (currentPOType === 'Incoming') {
                return (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Document (Upload / Update)</label>
                    {selectedPurchaseOrder?.pdf_path && (
                      <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm">
                          ✓ Current file: <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              openFileListModal(selectedPurchaseOrder)
                            }}
                            className="text-green-600 hover:text-green-800 underline font-medium"
                          >
                            View Uploaded File 📎
                          </button>
                        </p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => setSelectedDocument(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                    {selectedDocument && (
                      <p className="text-sm text-gray-600 mt-2">New file selected: {selectedDocument.name} (saved on Update)</p>
                    )}
                  </div>
                )
              }

              return null
            })()}

            {normalizeTypeLabel(formData.remarks_type) !== 'Outgoing' && (
              <div className="border-t pt-4 mt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    Edit project detail items here. Clicking Update will save these latest items back to the linked project.
                  </p>
                </div>

                <div ref={poItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 border-b border-gray-300">
                      <tr>
                        <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                        <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                        <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                        <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                        <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                        {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPoItems.map((item, idx) => (
                        <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(poItemsPage - 1) * poItemsRowsPerPage + idx + 1}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                          <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                          <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                          {!isViewer && (
                            <td className="px-2 py-1 text-center space-x-2">
                              <button
                                type="button"
                                onClick={() => startPoEditItem(item)}
                                className="inline-flex items-center justify-center w-8 h-8 text-blue-500 hover:text-blue-600 transition"
                                title="Edit item"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                onClick={() => removePoItem(item.id)}
                                className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition"
                                title="Delete item"
                              >
                                ❌
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {poEditingItemId === null && !isViewer && (
                        <tr className="border-b border-gray-200 bg-blue-50">
                          <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{poItems.length + 1}</td>
                          <td className="px-2 py-1">
                            <textarea
                              name="description"
                              value={poCurrentItem.description}
                              onChange={handlePoItemChange}
                              placeholder="Item description"
                              rows="2"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              name="price"
                              value={poCurrentItem.price}
                              onChange={handlePoItemChange}
                              placeholder="0.00"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              name="quantity"
                              value={poCurrentItem.quantity}
                              onChange={handlePoItemChange}
                              placeholder="0"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {poCurrentItem.price && poCurrentItem.quantity
                              ? formatCurrency(parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity))
                              : formatCurrency(0)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              type="button"
                              onClick={addPoItem}
                              title="Add new item"
                              className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 transition"
                            >
                              ➕
                            </button>
                          </td>
                        </tr>
                      )}
                      {poEditingItemId !== null && (
                        <tr className="border-b border-gray-200 bg-green-50">
                          <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-green-100 whitespace-nowrap">{poItems.findIndex((item) => item.id === poEditingItemId) + 1}</td>
                          <td className="px-2 py-1">
                            <textarea
                              name="description"
                              value={poCurrentItem.description}
                              onChange={handlePoItemChange}
                              placeholder="Item description"
                              rows="2"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              name="price"
                              value={poCurrentItem.price}
                              onChange={handlePoItemChange}
                              placeholder="0.00"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              name="quantity"
                              value={poCurrentItem.quantity}
                              onChange={handlePoItemChange}
                              placeholder="0"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {poCurrentItem.price && poCurrentItem.quantity
                              ? formatCurrency(parseFloat(poCurrentItem.price) * parseFloat(poCurrentItem.quantity))
                              : formatCurrency(0)}
                          </td>
                          <td className="px-2 py-1 text-center space-x-2">
                            <button
                              type="button"
                              onClick={() => savePoEditItem(poEditingItemId)}
                              title="Save changes"
                              className="inline-flex items-center justify-center w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded transition"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              onClick={cancelPoEditItem}
                              title="Cancel editing"
                              className="inline-flex items-center justify-center w-8 h-8 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      )}
                      {poItems.length === 0 && (
                        <tr>
                          <td colSpan={isViewer ? '5' : '6'} className="px-3 py-2 text-center text-gray-500">No detail items yet.</td>
                        </tr>
                      )}
                      {poItems.length > 0 && (
                        <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                          <td colSpan="4" className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                          <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getPoItemsTotal())}</td>
                          {!isViewer && <td></td>}
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <TablePagination
                    currentPage={poItemsPage}
                    totalPages={totalPoItemPages}
                    totalItems={poItems.length}
                    rowsPerPage={poItemsRowsPerPage}
                    onPrevious={() => setPoItemsPage((prev) => Math.max(prev - 1, 1))}
                    onNext={() => setPoItemsPage((prev) => Math.min(prev + 1, totalPoItemPages))}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t mt-6">
              <button
                onClick={handleUpdatePurchaseOrder}
                disabled={loading || !canMutate || !formData.po_value || !formData.po_date}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      <GeneratedPdfPreviewModal
        isOpen={Boolean(viewingPdfPath)}
        pdfUrl={viewingPdfPath}
        title="Purchase Order PDF"
        onClose={() => setViewingPdfPath(null)}
        onDownload={handleDownloadPreviewPdf}
      />

      <TemplateDocumentPreviewModal
        isOpen={Boolean(templatePreviewData)}
        template={templatePreviewData?.template}
        title={templatePreviewData?.title || 'Purchase Order Preview'}
        draft={templatePreviewData?.draft}
        items={templatePreviewData?.items}
        totals={templatePreviewData?.totals}
        onDownload={templatePreviewData ? handleDownloadTemplatePreviewPdf : undefined}
        onClose={() => setTemplatePreviewData(null)}
      />

      {showFileListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowFileListModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">Documents</h2>
              <button
                onClick={() => setShowFileListModal(false)}
                className="modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="space-y-3">
              {fileListModalData.files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                  </div>
                  <div className="flex gap-2 ml-2 flex-shrink-0">
                    <button
                      onClick={() => viewFileInModal(file.path, file)}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition"
                    >
                      View
                    </button>
                    <button
                      onClick={() => (file.templateExport
                        ? downloadPurchaseOrderTemplatePdf(fileListModalData.purchaseOrder)
                        : downloadFile(file.path, file.name))}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {viewingFileInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setViewingFileInModal(null)}>
          <div className="bg-white rounded-lg w-full h-full max-w-4xl max-h-screen overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800">File Viewer</h2>
              <button
                onClick={() => setViewingFileInModal(null)}
                className="modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {['pdf'].includes(getFileExtension(viewingFileInModal)) ? (
                <iframe
                  src={viewingFileInModal}
                  className="w-full h-full border-none"
                  title="File Viewer"
                />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(getFileExtension(viewingFileInModal)) ? (
                <img
                  src={viewingFileInModal}
                  alt="File"
                  className="max-w-none"
                />
              ) : (
                <div className="p-6 text-gray-600">
                  <p className="text-lg mb-2">Preview not available for this file type</p>
                  <p className="text-sm text-gray-500">Please download the file to view it.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {messageModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-800">{messageModal.title}</h3>
              <button
                type="button"
                onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{messageModal.message}</p>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setMessageModal({ open: false, title: 'Notification', message: '' })}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => {
          confirmModal.resolver && confirmModal.resolver(false)
          setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-800">{confirmModal.title}</h3>
              <button
                type="button"
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(false)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 whitespace-pre-line">{confirmModal.message}</p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(false)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmModal.resolver && confirmModal.resolver(true)
                  setConfirmModal({ open: false, title: 'Confirm', message: '', resolver: null })
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
